/**
 * REV-17972 — resolve a PSD log to a single order line using the configured
 * match fields.
 *
 * Pure: no org config reads, no mutation of its inputs. `populateDeliveryLogs`
 * owns the candidate list, the attach and the error reporting.
 */

import { Utils } from "revlock-webutils";
import { MatchField, MatchFieldValue } from "./ServiceDeliveryMatchConfig";

export interface MatchableLog {
    salesOrderItemId?: string;
    productCode?: string;
    linkedProductCode?: string;
    logDate: string;
    termStartDate?: string;
    termEndDate?: string;
}

export interface MatchableRaItem {
    salesOrderItemId?: string;
    salesOrderItem?: {
        id?: string;
        linkedProductCode?: string;
        product?: { code?: string };
    };
    deliveryStartDate: string;
    deliveryEndDate: string;
}

export type MatchErrorCode = "PSD_22" | "PSD_23" | "PSD_24";

export interface MatchCandidate {
    salesOrderItemId: string | undefined;
    deliveryStartDate: string;
    deliveryEndDate: string;
    reason: "date-ineligible" | "matched";
}

export type MatchResult<T extends MatchableRaItem> =
    | { raItem: T }
    | { code: MatchErrorCode; candidates: MatchCandidate[] };

type DateCompare = (a: string, b: string) => boolean;

const lineId = (raItem: MatchableRaItem): string | undefined =>
    raItem.salesOrderItemId ||
    (raItem.salesOrderItem && raItem.salesOrderItem.id);

const lineProductCode = (raItem: MatchableRaItem): string | undefined =>
    raItem.salesOrderItem &&
    raItem.salesOrderItem.product &&
    raItem.salesOrderItem.product.code;

const lineLinkedProductCode = (raItem: MatchableRaItem): string =>
    (raItem.salesOrderItem && raItem.salesOrderItem.linkedProductCode) || "";

const candidateDetail = (
    raItem: MatchableRaItem,
    reason: MatchCandidate["reason"]
): MatchCandidate => ({
    salesOrderItemId: lineId(raItem),
    deliveryStartDate: raItem.deliveryStartDate,
    deliveryEndDate: raItem.deliveryEndDate,
    reason
});

const uniqueMatch = <T extends MatchableRaItem>(matched: T[]): MatchResult<T> =>
    matched.length > 1
        ? {
              code: "PSD_24",
              candidates: matched.map((raItem) =>
                  candidateDetail(raItem, "matched")
              )
          }
        : { raItem: matched[0] };

/**
 * Fields first, then dates:
 * 1. Lines whose Product Code and Linked Product Code match the log. When none
 *    do, a line with no Linked Product Code whose Product Code equals the
 *    log's Linked Product Code (a standalone metered line) matches instead.
 * 2. With termDate, the lines that contain the whole PSD term.
 * 3. Only when no line contains the term, the lines active on the Log Date.
 *
 * @param log              one delivery log
 * @param candidateRaItems all active revenue arrangement items on the order
 * @param matchFields      parsed, non-empty list of match fields
 * @param endDateInclusive properties/ratable_end_date_inclusive
 * @returns { raItem } on a unique match, otherwise
 *          { code, candidates } where code is PSD_22 | PSD_23 | PSD_24.
 */
export function matchLogToLine<T extends MatchableRaItem>(
    log: MatchableLog,
    candidateRaItems: readonly T[],
    matchFields: readonly MatchFieldValue[],
    endDateInclusive: boolean
): MatchResult<T> {
    // A log that carries an Order Item Number attaches directly, as today.
    // There is no date check on this path.
    if (log.salesOrderItemId) {
        const direct = candidateRaItems.find(
            (raItem) => lineId(raItem) === log.salesOrderItemId
        );
        if (direct) {
            return { raItem: direct };
        }
        return { code: "PSD_22", candidates: [] };
    }

    const matchFieldSet = new Set<MatchFieldValue>(matchFields);
    const byLinkedProductCode = matchFieldSet.has(
        MatchField.LINKED_PRODUCT_CODE
    );

    let fieldMatched = candidateRaItems.filter((raItem) => {
        if (
            matchFieldSet.has(MatchField.PRODUCT_CODE) &&
            lineProductCode(raItem) !== log.productCode
        ) {
            return false;
        }
        if (
            byLinkedProductCode &&
            lineLinkedProductCode(raItem) !== (log.linkedProductCode || "")
        ) {
            return false;
        }
        return true;
    });

    // Tried only after the regular match fails: a plan line also has no Linked
    // Product Code, and its own entitlement lines must win over it.
    if (
        fieldMatched.length === 0 &&
        byLinkedProductCode &&
        log.linkedProductCode
    ) {
        fieldMatched = candidateRaItems.filter(
            (raItem) =>
                !lineLinkedProductCode(raItem) &&
                lineProductCode(raItem) === log.linkedProductCode
        );
    }

    if (fieldMatched.length === 0) {
        return { code: "PSD_22", candidates: [] };
    }

    if (
        matchFieldSet.has(MatchField.TERM_DATE) &&
        log.termStartDate &&
        log.termEndDate
    ) {
        const { termStartDate, termEndDate } = log;
        // Term End and the line's end date share one convention (both
        // inclusive or both exclusive), so a term may end on the line's end.
        const termMatched = fieldMatched.filter(
            (raItem) =>
                Utils.isSameOrAfter(termStartDate, raItem.deliveryStartDate) &&
                Utils.isSameOrBefore(termEndDate, raItem.deliveryEndDate)
        );
        if (termMatched.length > 0) {
            return uniqueMatch(termMatched);
        }
    }

    const beforeEndDate: DateCompare = endDateInclusive
        ? Utils.isSameOrBefore
        : Utils.isBefore;
    const logDateMatched = fieldMatched.filter(
        (raItem) =>
            Utils.isSameOrAfter(log.logDate, raItem.deliveryStartDate) &&
            beforeEndDate(log.logDate, raItem.deliveryEndDate)
    );

    if (logDateMatched.length === 0) {
        return {
            code: "PSD_23",
            candidates: fieldMatched.map((raItem) =>
                candidateDetail(raItem, "date-ineligible")
            )
        };
    }

    return uniqueMatch(logDateMatched);
}
